import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  ShieldCheck,
  AlertTriangle,
  FileX,
  FileWarning,
  Hourglass,
  ExternalLink,
  RotateCw,
} from "lucide-react";

interface SanityFinding {
  pulseId: number;
  projectId: string;
  projectName: string | null;
  filename: string;
  reason: string;
  recordedSize?: number;
  actualSize?: number;
  originalIndex?: number;
  retryable?: boolean;
  sourceGone?: boolean;
}

interface SanityReport {
  ranAt: string;
  durationMs: number;
  scannedPulses: number;
  scannedRecords: number;
  checkedProjectFolders: number;
  findings: {
    uploadFailed: SanityFinding[];
    missing: SanityFinding[];
    sizeMismatch: SanityFinding[];
    stuckProcessing: SanityFinding[];
  };
  unverifiedRecords: number;
  skippedInFlight: number;
  truncatedFindings: Record<string, number>;
  orphanFileCount: number;
  scanErrors: string[];
}

const CATEGORY_META: Array<{
  key: keyof SanityReport["findings"];
  label: string;
  hint: string;
  icon: React.ReactNode;
}> = [
  {
    key: "uploadFailed",
    label: "Upload failed",
    hint: "Marked broken when the upload happened (e.g. Dropbox storage full)",
    icon: <FileX className="w-3.5 h-3.5 text-red-400" />,
  },
  {
    key: "missing",
    label: "Missing in Dropbox",
    hint: "The record points at a file that no longer exists in Dropbox",
    icon: <FileWarning className="w-3.5 h-3.5 text-orange-400" />,
  },
  {
    key: "sizeMismatch",
    label: "Wrong size",
    hint: "File exists but its size differs from what was recorded",
    icon: <AlertTriangle className="w-3.5 h-3.5 text-yellow-400" />,
  },
  {
    key: "stuckProcessing",
    label: "Stuck processing",
    hint: "Attachment processing never finished for these pulses",
    icon: <Hourglass className="w-3.5 h-3.5 text-sky-400" />,
  },
];

function openPulse(pulseId: number) {
  window.dispatchEvent(new CustomEvent("open-pulse-workbench", { detail: { pulseId } }));
}

export function AttachmentSanityCheck() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [report, setReport] = useState<SanityReport | null>(null);
  // Task #174/#175: per-finding retry state, keyed by `${pulseId}:${originalIndex}`.
  // 'retrying' shows a spinner; 'error' marks rows whose bulk retry failed
  // (transient) so the admin can see which rows still need attention.
  const [rowStatus, setRowStatus] = useState<Record<string, "retrying" | "error">>({});
  const [bulkRunning, setBulkRunning] = useState(false);
  // Task #176: abort flag for the bulk run. Workers check it before dequeuing
  // the next finding — in-flight retries finish, no new ones start.
  const bulkStopRef = useRef(false);
  const [bulkStopping, setBulkStopping] = useState(false);

  const findingKey = (f: SanityFinding) => `${f.pulseId}:${f.originalIndex}`;

  const setRowState = (key: string, status: "retrying" | "error" | null) =>
    setRowStatus((prev) => {
      const next = { ...prev };
      if (status === null) delete next[key];
      else next[key] = status;
      return next;
    });

  const removeFinding = (finding: SanityFinding) =>
    setReport((prev) =>
      prev
        ? {
            ...prev,
            findings: {
              ...prev.findings,
              uploadFailed: prev.findings.uploadFailed.filter(
                (f) =>
                  !(f.pulseId === finding.pulseId && f.originalIndex === finding.originalIndex)
              ),
            },
          }
        : prev
    );

  const markSourceGone = (finding: SanityFinding) =>
    setReport((prev) =>
      prev
        ? {
            ...prev,
            findings: {
              ...prev.findings,
              uploadFailed: prev.findings.uploadFailed.map((f) =>
                f.pulseId === finding.pulseId && f.originalIndex === finding.originalIndex
                  ? { ...f, retryable: false, sourceGone: true }
                  : f
              ),
            },
          }
        : prev
    );

  // Core retry for a single finding. Returns the outcome so bulk retry can
  // tally a summary. When `silent`, per-row toasts are suppressed (the bulk
  // run reports a single summary toast instead).
  const retryOne = async (
    finding: SanityFinding,
    opts: { silent?: boolean } = {}
  ): Promise<"repaired" | "sourceGone" | "failed"> => {
    const key = findingKey(finding);
    setRowState(key, "retrying");
    try {
      const res = await apiRequest(
        "POST",
        `/api/pulses/${finding.pulseId}/attachments/${finding.originalIndex}/retry`
      );
      const data = await res.json();
      if (!opts.silent) {
        toast({ title: `Re-uploaded ${data.attachment?.filename || finding.filename}` });
      }
      // If the pulse workbench is open (or opened later from cache), make it
      // refetch so the repaired attachment shows up there too.
      queryClient.invalidateQueries({ queryKey: ["pulse-workbench", finding.pulseId] });
      // Repaired — remove the finding from the report.
      removeFinding(finding);
      setRowState(key, null);
      return "repaired";
    } catch (error: any) {
      if (error?.status === 410 || (error instanceof Error && error.message.startsWith("410"))) {
        if (!opts.silent) {
          toast({
            title: "Source email is gone",
            description:
              "The original email is no longer available — please attach the file manually.",
            variant: "destructive",
          });
        }
        // Terminal — flip the finding to the "attach manually" state.
        markSourceGone(finding);
        setRowState(key, null);
        return "sourceGone";
      }
      if (!opts.silent) {
        toast({
          title: "Retry failed",
          description: "Temporary problem reaching the mailbox or Dropbox — please try again.",
          variant: "destructive",
        });
        setRowState(key, null);
      } else {
        // Bulk run: keep a visible per-row error marker.
        setRowState(key, "error");
      }
      return "failed";
    }
  };

  const retryMutation = useMutation({
    mutationFn: (finding: SanityFinding) => retryOne(finding),
  });

  // Task #175: retry every retryable "Upload failed" finding with bounded
  // concurrency, then report a summary toast.
  const RETRY_ALL_CONCURRENCY = 3;
  const retryAll = async () => {
    if (!report || bulkRunning) return;
    const targets = report.findings.uploadFailed.filter((f) => f.retryable);
    if (targets.length === 0) return;
    setBulkRunning(true);
    bulkStopRef.current = false;
    setBulkStopping(false);
    // Clear stale error markers from a previous bulk run.
    setRowStatus({});
    const counts = { repaired: 0, sourceGone: 0, failed: 0 };
    try {
      let next = 0;
      const worker = async () => {
        // Task #176: check the stop flag before dequeuing the next finding so
        // a stopped run finishes in-flight retries but starts no new ones.
        while (!bulkStopRef.current && next < targets.length) {
          const finding = targets[next++];
          const outcome = await retryOne(finding, { silent: true });
          counts[outcome]++;
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(RETRY_ALL_CONCURRENCY, targets.length) }, worker)
      );
      const stopped = bulkStopRef.current;
      const skipped = targets.length - (counts.repaired + counts.sourceGone + counts.failed);
      const parts = [`${counts.repaired} repaired`];
      if (counts.sourceGone > 0) parts.push(`${counts.sourceGone} need manual attachment`);
      if (counts.failed > 0) parts.push(`${counts.failed} failed`);
      if (stopped && skipped > 0) parts.push(`${skipped} skipped`);
      toast({
        title: stopped ? "Retry all stopped" : "Retry all finished",
        description: parts.join(", "),
        variant: counts.failed > 0 ? "destructive" : undefined,
      });
    } finally {
      setBulkRunning(false);
      bulkStopRef.current = false;
      setBulkStopping(false);
    }
  };

  const stopRetryAll = () => {
    bulkStopRef.current = true;
    setBulkStopping(true);
  };

  const runMutation = useMutation({
    mutationFn: async (): Promise<SanityReport> => {
      const res = await apiRequest("POST", "/api/admin/attachment-sanity/run");
      return res.json();
    },
    onSuccess: (data) => setReport(data),
    onError: (err: any) => {
      toast({
        title: "Sanity check failed",
        description: err?.message || "Could not run the scan",
        variant: "destructive",
      });
    },
  });

  const totalFindings = report
    ? CATEGORY_META.reduce((n, c) => n + report.findings[c.key].length, 0)
    : 0;

  return (
    <div className="space-y-4" data-testid="attachment-sanity-check">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-gray-400">
          Compares every pulse attachment record against what actually exists in
          Dropbox: failed uploads, files missing or truncated in Dropbox, and
          pulses whose attachment processing never finished. The scan itself is
          read-only; failed email attachments can be retried directly from the
          report.
        </p>
        <Button
          onClick={() => runMutation.mutate()}
          disabled={runMutation.isPending}
          className="shrink-0"
          data-testid="button-run-sanity-check"
        >
          {runMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Scanning…
            </>
          ) : (
            <>
              <ShieldCheck className="w-4 h-4 mr-2" />
              Run check
            </>
          )}
        </Button>
      </div>

      {runMutation.isPending && (
        <p className="text-xs text-gray-500">
          Listing project folders in Dropbox — this can take a moment for large accounts.
        </p>
      )}

      {report && !runMutation.isPending && (
        <div className="space-y-3">
          <div className="text-xs text-gray-500">
            Checked {report.scannedRecords} attachment record
            {report.scannedRecords === 1 ? "" : "s"} across {report.scannedPulses} pulse
            {report.scannedPulses === 1 ? "" : "s"} and {report.checkedProjectFolders} project folder
            {report.checkedProjectFolders === 1 ? "" : "s"} in{" "}
            {(report.durationMs / 1000).toFixed(1)}s.
            {report.orphanFileCount > 0 && (
              <> {report.orphanFileCount} file{report.orphanFileCount === 1 ? "" : "s"} in pulse folders had no matching record (info only).</>
            )}
          </div>

          {report.scanErrors.length > 0 && (
            <div className="rounded-md border border-yellow-600/50 bg-yellow-900/20 p-2 text-xs text-yellow-300 space-y-1">
              <div className="font-medium">
                Partial result — {report.unverifiedRecords} record
                {report.unverifiedRecords === 1 ? "" : "s"} could not be verified:
              </div>
              {report.scanErrors.map((e, i) => (
                <div key={i} className="truncate">{e}</div>
              ))}
            </div>
          )}

          {report.skippedInFlight > 0 && (
            <div className="text-xs text-gray-500">
              {report.skippedInFlight} record{report.skippedInFlight === 1 ? "" : "s"} skipped
              (upload still in progress or no verifiable path) — not counted as verified.
            </div>
          )}

          {totalFindings === 0 ? (
            report.unverifiedRecords === 0 && report.scanErrors.length === 0 ? (
              <div className="flex items-center gap-2 rounded-md border border-green-600/50 bg-green-900/20 p-3 text-sm text-green-300" data-testid="sanity-all-good">
                <ShieldCheck className="w-4 h-4" />
                All good — every verified attachment record matches Dropbox.
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-md border border-gray-600 bg-gray-800 p-3 text-sm text-gray-300" data-testid="sanity-partial">
                <AlertTriangle className="w-4 h-4 text-yellow-400" />
                No problems in the verified records — but the scan was incomplete (see above), so this is not a full all-clear.
              </div>
            )
          ) : (
            CATEGORY_META.map(({ key, label, hint, icon }) => {
              const items = report.findings[key];
              if (items.length === 0) return null;
              const retryableCount =
                key === "uploadFailed" ? items.filter((f) => f.retryable).length : 0;
              return (
                <div key={key} className="rounded-md border border-gray-600 overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-700/50">
                    {icon}
                    <span className="text-sm font-medium text-white">{label}</span>
                    <span className="px-1.5 rounded-full bg-gray-600 text-[11px] text-gray-200">
                      {items.length}
                      {report.truncatedFindings?.[key] ? ` (+${report.truncatedFindings[key]} more)` : ""}
                    </span>
                    <span className="text-[11px] text-gray-400 truncate">{hint}</span>
                    {key === "uploadFailed" && bulkRunning ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-[11px] ml-auto shrink-0"
                        disabled={bulkStopping}
                        onClick={stopRetryAll}
                        data-testid="sanity-retry-all-stop"
                      >
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        {bulkStopping ? "Stopping…" : "Stop"}
                      </Button>
                    ) : (
                      retryableCount >= 2 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-[11px] ml-auto shrink-0"
                          disabled={retryMutation.isPending}
                          onClick={retryAll}
                          data-testid="sanity-retry-all"
                        >
                          <RotateCw className="w-3 h-3 mr-1" />
                          Retry all ({retryableCount})
                        </Button>
                      )
                    )}
                  </div>
                  <div className="divide-y divide-gray-700 max-h-56 overflow-y-auto">
                    {items.map((f, i) => {
                      const retryKey = `${f.pulseId}:${f.originalIndex}`;
                      const isRetrying = rowStatus[retryKey] === "retrying";
                      const rowFailed = rowStatus[retryKey] === "error";
                      return (
                        <div
                          key={`${f.pulseId}-${i}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => openPulse(f.pulseId)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") openPulse(f.pulseId);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-700/60 transition-colors cursor-pointer"
                          title="Open pulse"
                          data-testid={`sanity-finding-${f.pulseId}-${i}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-xs text-gray-200 truncate">
                              {f.filename}
                            </div>
                            <div className="text-[11px] text-gray-500 truncate">
                              {f.projectName ?? f.projectId} · Pulse {f.pulseId} — {f.reason}
                            </div>
                            {f.sourceGone && (
                              <div className="text-[11px] text-orange-400 truncate">
                                Source email is gone — please attach the file manually.
                              </div>
                            )}
                            {rowFailed && (
                              <div className="text-[11px] text-red-400 truncate" data-testid={`sanity-row-error-${f.pulseId}-${f.originalIndex}`}>
                                Retry failed — temporary problem, try again.
                              </div>
                            )}
                          </div>
                          {f.retryable && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-[11px] shrink-0"
                              disabled={retryMutation.isPending || bulkRunning}
                              onClick={(e) => {
                                e.stopPropagation();
                                retryMutation.mutate(f);
                              }}
                              data-testid={`sanity-retry-${f.pulseId}-${f.originalIndex}`}
                            >
                              {isRetrying ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <RotateCw className="w-3 h-3 mr-1" />
                              )}
                              Retry
                            </Button>
                          )}
                          <ExternalLink className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
